import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const momentumPivot: WikiTopic = {
  slug: "momentum-pivot",
  title: "Momentum pivot",
  summary: "Price entering a Central Pivot Range zone with Wilder RSI agreeing.",
  group: "strategies",
  horizon: "session",
  strategyKey: "momentum_pivot",
  sections: [
    {
      heading: "What it looks for",
      code: "pivot_bounce / pivot_rejection",
      blocks: [
        p(
          <>
            The Central Pivot Range comes from the previous completed daily session, never from the
            intraday frame. CPR is a daily construct: the pivot is defined on the previous session's
            high, low, and close. The reference computes it from the last row of whatever series it
            is handed, so on a 15-minute frame it produces a "central pivot range" of one 15-minute
            candle.
          </>,
        ),
        ul([
          <>
            Bounce: price sits between <Datum>S1</Datum> and the pivot with Wilder RSI-
            <Datum>{QUOTED.MOMENTUM_RSI_PERIOD}</Datum> below{" "}
            <Datum>{QUOTED.MOMENTUM_RSI_OVERSOLD}</Datum>. Side is long.
          </>,
          <>
            Rejection mirrors it: price between the pivot and <Datum>R1</Datum> with RSI above{" "}
            <Datum>{QUOTED.MOMENTUM_RSI_OVERBOUGHT}</Datum>. Side is short.
          </>,
        ]),
        p(
          <>
            It needs <Datum>{QUOTED.MOMENTUM_PIVOT_MIN_BARS}</Datum> buckets, five times the RSI
            window. A Wilder RSI two bars past its seed is numerically dominated by that seed, and
            seeding bias lands hardest exactly where a threshold crossing is decided.
          </>,
        ),
      ],
    },
    {
      heading: "The gates",
      blocks: [
        table(
          "Momentum pivot gates",
          ["Gate", "Value"],
          [
            ["RSI period (Wilder)", <Datum key="p">{QUOTED.MOMENTUM_RSI_PERIOD}</Datum>],
            ["Oversold floor", <Datum key="o">{QUOTED.MOMENTUM_RSI_OVERSOLD}</Datum>],
            ["Overbought ceiling", <Datum key="b">{QUOTED.MOMENTUM_RSI_OVERBOUGHT}</Datum>],
            ["Minimum history", <Datum key="m">{QUOTED.MOMENTUM_PIVOT_MIN_BARS} buckets</Datum>],
            ["Pivot source", "Previous completed daily session"],
            ["Trigger", "Entry into the zone, not the zone holding"],
          ],
        ),
      ],
    },
    {
      heading: "Why it is edge-triggered",
      blocks: [
        p(
          <>
            "Price between S1 and the pivot with RSI under{" "}
            <Datum>{QUOTED.MOMENTUM_RSI_OVERSOLD}</Datum>" is a state, not an event. Once true it
            stays true for hours. So the same zone held at the previous bucket of the same session
            suppresses the firing: a condition that is still true is not a second opinion arriving.
            The window is the session, so yesterday's last bucket cannot suppress today's first.
          </>,
        ),
        p(
          <>
            The case that caught it: <Datum>BIKAJI</Datum> on <Datum>2026-09-02</Datum> held between
            S1 <Datum>587.03</Datum> and pivot <Datum>603.27</Datum> with RSI under the floor for
            all three buckets of the session, so <Datum>pivot_bounce</Datum> returned long three
            times.
          </>,
        ),
        p(
          <>
            Two things went wrong and only one was visible. The unique index on exchange, symbol,
            signal type, and session date dropped the 10:15 and 11:15 firings, so the chart drew one
            dot at 09:15. The invisible failure is worse: confluence scores the evaluator's results
            rather than the stored rows, so the 12:15 bucket counted a momentum pivot vote that had
            been true since the open and wrote a delivered confluence row asserting two strategies
            agreed now, on the strength of one that agreed three hours earlier.
          </>,
        ),
        note(
          <>
            Firing on entry also makes each observation one event, which is what the forward-return
            measurement needs. A strategy that re-asserts a standing condition every bucket reports
            its own persistence as a hit rate.
          </>,
        ),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        p(
          <>
            Magnitude is the distance past the threshold that was crossed, so both directions sit on
            one scale. A bounce carries the oversold floor minus RSI; a rejection carries RSI minus
            the overbought ceiling.
          </>,
        ),
        p(
          <>
            The target is the pivot in both directions. The stop sits a fifth of the pivot-to-S1
            distance below S1 on a bounce, and the same fraction of the pivot-to-R1 distance above
            R1 on a rejection.
          </>,
        ),
      ],
    },
  ],
};
